import React, { useCallback, useMemo, useState } from 'react';
import { Image } from 'semantic-ui-react';
import Visibility from '../../components/Visibility';
import './style.scss';
import { Description, SubTitle } from '../../components/layouts';
import Sequence from '../../components/sequence';
import { buildImageURL, getSeqFrames, getTrack, pureTrans } from '../../helpers';
import { AppContext } from '../../Context';
import { SeqencePlaceholder } from '../../components/sequence/Placeholder';

const FRAME_LENGTH = 61;
const IMAGES = {
  finger: {
    d: buildImageURL('/finger-d.png'),
    m: buildImageURL('/finger-m.png')
  }
};

const track = getTrack({ block: 3 });

export function Lightweight() {
  const [mobile, setMobile] = useState(false);
  const [frozen, freeze] = useState(true);
  const [progress, setProgress] = useState(0);
  const [framesLoaded, setFramesLoaded] = React.useState(false);

  const { webpSupport, webpDetectionFinished } = React.useContext(AppContext);

  const frames = useMemo(() => {
    return getSeqFrames({
      prefix: 'lightweight',
      mobile,
      webpSupport: false,
      ext: 'jpg',
      len: FRAME_LENGTH
    });
  }, [webpSupport, mobile]);

  const [index, firstTextOpacity, secondTextOpacity, firstTextY, secondTextY, fingerX, phoneX, fullOpacity] = useMemo(() => {
    const ftt = mobile ? [-10, -15, -20] : [25, 20, 15];
    const stt = mobile ? [-5, -10, -15, -22] : [15, 10, 5, -2];
    return [
      //index
      pureTrans(progress, [0.0, 0.4], [0, FRAME_LENGTH - 1], 0),
      // fto
      pureTrans(progress, [0.0, 0.1, 0.2], [0, 1, 0], 3),
      // sto
      mobile ? pureTrans(progress, [0.2, 0.3, 0.4], [0, 1, 0], 3) : pureTrans(progress, [0.2, 0.3, 0.4], [0, 1, 0], 3),
      // fty
      pureTrans(progress, [0, 0.1, 0.2], ftt, 3),
      // sty
      pureTrans(progress, [0, 0.2, 0.3, 0.4], stt, 3),
      // fx
      mobile ? pureTrans(progress, [0.3, 0.5, 0.7], [100, 67, 37], 3) : pureTrans(progress, [0.3, 0.5, 0.7], [100, 83.5, 44], 3),
      // px
      mobile ? pureTrans(progress, [0.5, 0.7], [0, 30], 3) : pureTrans(progress, [0.5, 0.7], [0, 40], 3),
      // fo
      pureTrans(progress, [0.7, 0.9], [1, 0.2], 3)
    ];
  }, [progress, mobile]);

  const handleUpdate = useCallback(
    (e, { calculations }) => {
      const { percentagePassed, pixelsPassed, height, offScreen, width } = calculations;
      freeze(offScreen || pixelsPassed <= -1000 || pixelsPassed >= height + 1000);
      setMobile(width <= 750);
      setProgress(percentagePassed);
    },
    [setMobile, setProgress, freeze]
  );
  const onFramesLoaded = useCallback(() => {
    setFramesLoaded(true);
  }, [setFramesLoaded]);

  const seqReady = !frozen && webpDetectionFinished;
  const allReady = seqReady && framesLoaded;
  return (
    <Visibility
      fireOnMount
      offset={[-1000, -1000]}
      onUpdate={handleUpdate}
      className="page-section inverted section-lightweight"
      data-track={track}
    >
      <div className="sticky-content">
        <div className="height-holder" />
        <div className="section-content">
          <div className="first-text" style={{ width: '100%', opacity: firstTextOpacity, transform: `translate(-50%, ${firstTextY}%)` }}>
            <SubTitle>乐享丝滑触感</SubTitle>
            <Description>惊艳，从触到它的第一刻开始。前后3D曲面屏设计，一触倾心，不留痕迹。</Description>
          </div>
          <div className="second-text" style={{ width: '100%', opacity: secondTextOpacity, transform: `translate(-50%, ${secondTextY}%)` }}>
            <SubTitle>
              又瘦了
              <br />
              拿在手里更轻了
            </SubTitle>
            <Description>
              7.59mm，仅约178g<sup>1</sup>，你见过的vivo最薄3D曲面屏手机。
            </Description>
          </div>
        </div>
        <div className="section-background" style={{ opacity: fullOpacity }}>
          <div className="sequence-mover" style={{ transform: `translateX(${phoneX}%)` }}>
            <div className="sequence-wrapper">
              <Sequence current={index} frames={frames} active={!frozen && webpDetectionFinished} onload={onFramesLoaded} />
              <SeqencePlaceholder src={frames[frames.length - 1]} visible={!allReady} />
            </div>
          </div>
          {allReady && (
            <Image
              src={mobile ? IMAGES.finger.m : IMAGES.finger.d}
              className="big-eye-finger"
              style={{ transform: `translateY(${mobile ? '-90%' : '-85%'}) translateX(-${fingerX}%)` }}
            />
          )}
        </div>
      </div>
    </Visibility>
  );
}
